// Publish's two threads (design §2.6): the page, which holds the live documents and makes every edit, and the compile
// worker (`worker.ts` over `host.ts`), which runs Select/Map, the writers, preflight and the print loop on its own copy
// of the documents, so typing never waits for a build. The page sends the documents it changed since the last request
// (whole when their history was replaced, else the changes since); the worker asks the page to record each compile's
// checkpoint (the page's edit, which syncs like any other) and to typeset through the one Typst worker the page owns.
import type { LayoutRun } from '@writing-suite/compile/typst';
import type {
  CompileReport, LastPrint, MarketRules, PackDocument, PreflightFinding, PublishFormat, ReadinessItem,
} from '@writing-suite/compile';

/** A document the worker should hold: `full` replaces its copy, else `bytes` are the changes since the last. */
export interface DocUpdate { name: string; bytes: Uint8Array; full: boolean }

/** The writer's own details (the web knows the account's name; contact details are the Mac's). */
export interface Writer { name: string; surname?: string; contact?: string[] }

/** What a page of Typst's output looks like to the preview: every line where Typst set it, in points. */
export interface LaidOutPage {
  width: number;
  height: number;
  lines: { x: number; y: number; width: number; size: number; text: string; font: string; gaps: number[][]; runs?: LayoutRun[] }[];
}

export interface AudiobookTrack { id: number; title: string; seconds: number }
export interface AudiobookEstimate { tracks: AudiobookTrack[]; total: number; sampleTitle: string; sampleText: string }

/** What the page shows before anything's built (`PublishPreview`). */
export type Preview =
  | { kind: 'pdf'; pdf: Uint8Array; pages: LaidOutPage[]; book: boolean }
  | { kind: 'html'; html: string }
  | { kind: 'text'; text: string }
  | { kind: 'tracks'; estimate: AudiobookEstimate }
  | { kind: 'message'; message: string };

export interface FormatList {
  builtIn: PublishFormat[];
  more: PublishFormat[];
  yours: PublishFormat[];
  /** The project's presets that can't be read, and why. */
  problems: { id: string; message: string }[];
}

/** A prepared (not rendered) manuscript, as the details column needs it. */
export interface PreparedSummary {
  title: string;
  wordCount: number;
  presetId: string;
  presetName: string;
  format: CompileReport['format'];
  blind: boolean;
  isDocument: boolean;
  isPost: boolean;
  print: boolean;
  screenplay: boolean;
  audio: boolean;
  frontMatter: boolean;
  postMatter: boolean;
  /** Who it's published as, and whether that's a pen name. */
  publishedAs: string | null;
  penName: boolean;
  /** The edition's ISBN and publisher, as the copyright page will give them (read-only here). */
  publication: { edition: string | null; isbn: string; publisher: string; copyright: string };
}

/** `PublishPreparation`. */
export interface Preparation {
  summary: PreparedSummary | null;
  readiness: ReadinessItem[];
  documents: PackDocument[];
  /** The settings summary, from the resolved preset, in the writer's terms. */
  settings: [string, string][];
  error: string | null;
}

export interface BuiltFile { name: string; bytes: Uint8Array; mime: string }

export interface BuildResult {
  files: BuiltFile[];
  reports: CompileReport[];
  findings: PreflightFinding[];
  /** A print build: what it fixed and its page count, for `lastPrint-<project>`. */
  lastPrint: LastPrint | null;
  /** PDF page count. */
  pages: number | null;
  /** A print build's pages as Typst laid them out (Page links in preflight jump to them). */
  layout: LaidOutPage[] | null;
  /** A submission pack: every document in one zip. */
  zip: { name: string; bytes: Uint8Array } | null;
}

export interface Selection {
  formatKind: PublishFormat['kind'];
  presetId: string;
  writer: Writer;
  /** The journal post a one-post preset builds (a fragment id). */
  post: string | null;
}

export type Request =
  | { kind: 'formats' }
  | ({ kind: 'prepare'; lastPrint: LastPrint | null } & Selection)
  | ({ kind: 'preview'; cachedKey: string | null } & Selection)
  | ({ kind: 'build'; market: MarketRules | null; locale?: string; limit?: string[] | null } & Selection)
  | { kind: 'pack'; presetIds: string[]; writer: Writer; locale?: string };

export type Response =
  | FormatList
  | Preparation
  | { key: string; preview: Preview | null }
  | BuildResult;

/** The worker asks the page. */
export type Call =
  | { kind: 'checkpoint'; name: string }
  | { kind: 'typst'; op: 'setFonts' | 'compilePDF' | 'compileLayout' | 'evict'; args: unknown[] };

export interface Progress { pass: number; of: number; pages: number }

/** Page → worker. */
export type ToHost =
  | { t: 'init'; typst: boolean }
  | { t: 'sync'; updates: DocUpdate[]; removed: string[] }
  | { t: 'request'; id: number; request: Request }
  | { t: 'reply'; id: number; ok: true; result: unknown }
  | { t: 'reply'; id: number; ok: false; error: string };

/** Worker → page. */
export type FromHost =
  | { t: 'response'; id: number; ok: true; result: Response }
  | { t: 'response'; id: number; ok: false; error: string }
  | { t: 'call'; id: number; call: Call }
  | { t: 'progress'; id: number; progress: Progress };

/** One end of the channel: a Worker, `self` in the worker, or a pair in memory (tests). */
export interface Port<In, Out> {
  post(message: Out, transfer?: Transferable[]): void;
  listen(fn: (message: In) => void): void;
}

/** The buffers in a message worth moving rather than copying. */
export function transferables(value: unknown, out: Transferable[] = [], depth = 0): Transferable[] {
  if (depth > 6 || value === null || typeof value !== 'object') return out;
  if (value instanceof Uint8Array) {
    if (value.byteOffset === 0 && value.byteLength === value.buffer.byteLength && value.buffer instanceof ArrayBuffer && !out.includes(value.buffer)) out.push(value.buffer);
    return out;
  }
  if (Array.isArray(value)) {
    for (const v of value) transferables(v, out, depth + 1);
    return out;
  }
  for (const v of Object.values(value)) transferables(v, out, depth + 1);
  return out;
}
